import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useFeatureFlags } from "../context/FeatureFlagContext";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { useUser } from "./UserContext.jsx";
import { PERMISSIONS } from "../permissions";
import { canViewPlatformTelemetry } from "../rbacUiPolicy";

import {
  getConfig,
  getMnemoLiveDashboard,
  getNeedlepathMetricsSummary,
  listAgents,
  listTools,
  listWorkflows,
} from "../api/agentApi";

const COPPER_DARK = BRAND_COLORS.primary.dark;
const COPPER_SOFT = BRAND_COLORS.primary.soft;
const BORDER_SOFT = "#E5D4C4";
const BORDER_SUBTLE = "#E8E2DA";
const GREEN = "#2F7D4A";
const GREEN_SOFT = "rgba(47, 125, 74, 0.12)";
const BLUE = "#1F78D1";
const BLUE_SOFT = "rgba(31, 120, 209, 0.12)";
const AMBER = "#9A6A16";
const AMBER_SOFT = "rgba(154, 106, 22, 0.12)";
const RED = "#B13A2E";
const RED_SOFT = "rgba(177, 58, 46, 0.10)";
const MUTED = "#6F6A64";
const SURFACE = "#FFFEFC";

type Tone = "green" | "blue" | "copper" | "neutral" | "amber" | "red";

type Agent = {
  status?: string;
  agent_source?: string;
  catalog_metadata?: Record<string, unknown> | null;
};

type Workflow = {
  status?: string;
};

type Tool = {
  status?: string;
  keys_schema?: unknown[];
  auth_required?: boolean | null;
  setup_requirements?: unknown[];
  exportable?: boolean;
};

type DashboardCardProps = {
  title: string;
  eyebrow: string;
  descriptor?: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  to: string;
  action: string;
  chips?: Array<{ label: string; tone?: Tone }>;
  beta?: boolean;
  live?: boolean;
};

type SignalItemProps = {
  title: string;
  detail: string;
  tone?: Tone;
  to?: string;
  action?: string;
};

const normalizeStatus = (status?: string) => String(status || "").trim().toLowerCase();

const compactNumber = (value: number) =>
  new Intl.NumberFormat("en", { notation: value >= 10000 ? "compact" : "standard" }).format(value || 0);

const pluralize = (count: number, singular: string, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

const toNumber = (...values: unknown[]) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return 0;
};

const agentNeedsApiSetup = (agent: Agent) => {
  if (normalizeStatus(agent.status) === "ready") return false;
  return normalizeStatus(agent.status) === "setup_required" || Boolean(agent.catalog_metadata?.requires_api_setup);
};

const toolNeedsSetup = (tool: Tool) =>
  Boolean(tool.auth_required) ||
  Boolean(tool.keys_schema?.length) ||
  Boolean(tool.setup_requirements?.length);

const getToneStyles = (tone: Tone, isDark: boolean): { bgcolor: string; color: string; border: string } => {
  const lightToneStyles: Record<Tone, { bgcolor: string; color: string; border: string }> = {
    green: { bgcolor: GREEN_SOFT, color: GREEN, border: "rgba(47,125,74,0.24)" },
    blue: { bgcolor: BLUE_SOFT, color: BLUE, border: "rgba(31,120,209,0.24)" },
    copper: { bgcolor: COPPER_SOFT, color: COPPER_DARK, border: "rgba(184,115,51,0.24)" },
    amber: { bgcolor: AMBER_SOFT, color: AMBER, border: "rgba(154,106,22,0.24)" },
    red: { bgcolor: RED_SOFT, color: RED, border: "rgba(177,58,46,0.22)" },
    neutral: { bgcolor: "#F6F1EC", color: MUTED, border: "rgba(111,106,100,0.14)" },
  };

  if (!isDark) return lightToneStyles[tone];

  return {
    green: { bgcolor: alpha(GREEN, 0.22), color: "#CFEFD4", border: "rgba(145,205,153,0.30)" },
    blue: { bgcolor: alpha(BLUE, 0.20), color: "#CDE6FF", border: "rgba(140,190,238,0.30)" },
    copper: { bgcolor: "rgba(224,177,132,0.16)", color: "#F2C69B", border: "rgba(224,177,132,0.30)" },
    amber: { bgcolor: "rgba(255,184,89,0.16)", color: "#FFE2BD", border: "rgba(255,184,89,0.30)" },
    red: { bgcolor: "rgba(239,99,99,0.16)", color: "#FFDADA", border: "rgba(239,99,99,0.28)" },
    neutral: { bgcolor: "rgba(255,247,239,0.10)", color: "#D9C9BA", border: "rgba(255,247,239,0.16)" },
  }[tone];
};

function MetricChip({ label, tone = "neutral" }: { label: string; tone?: Tone }) {
  const theme = useTheme();
  const tones = getToneStyles(tone, theme.palette.mode === "dark");

  return (
    <Chip
      size="small"
      label={label}
      data-tone={tone}
      sx={{
        bgcolor: tones.bgcolor,
        color: tones.color,
        border: `1px solid ${tones.border}`,
        borderRadius: UI_RADIUS.chip,
        fontWeight: 850,
      }}
    />
  );
}

function SignalItem({ title, detail, tone = "neutral", to, action }: SignalItemProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tones = getToneStyles(tone, isDark);
  const actionColor = isDark
    ? tone === "red"
      ? "#FFB4A9"
      : tone === "blue"
        ? "#9FD0FF"
        : "#F2C69B"
    : tone === "red"
      ? RED
      : tone === "blue"
        ? BLUE
        : COPPER_DARK;

  return (
    <Box
      data-tone={tone}
      sx={{
        display: "grid",
        gridTemplateColumns: "12px minmax(0, 1fr)",
        gap: 1.25,
        py: 1.2,
      }}
    >
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: UI_RADIUS.iconButton,
          bgcolor: tones.color,
          mt: 0.8,
          boxShadow: `0 0 0 4px ${tones.bgcolor}`,
        }}
      />
      <Box>
        <Typography component="p" variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.25 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35, lineHeight: 1.45 }}>
          {detail}
        </Typography>
        {to && action && (
          <Button
            component={RouterLink}
            to={to}
            endIcon={<ArrowForwardIcon />}
            sx={{
              mt: 0.6,
              px: 0,
              minWidth: 0,
              color: actionColor,
              textTransform: "none",
              fontWeight: 900,
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            {action}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function SectionShell({
  title,
  description,
  children,
  testId,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <Box
      data-testid={testId}
      sx={(theme) => ({
        borderRadius: UI_RADIUS.panel,
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(224,177,132,0.22)" : BORDER_SUBTLE}`,
        bgcolor: theme.palette.mode === "dark" ? "background.paper" : "rgba(255,255,255,0.78)",
        boxShadow: theme.palette.mode === "dark" ? "0 18px 54px rgba(0,0,0,0.28)" : "none",
        color: "text.primary",
        p: { xs: 2, md: 2.5 },
      })}
    >
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.75 }}>
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 950, letterSpacing: 0 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function DashboardCard({
  title,
  eyebrow,
  descriptor,
  value,
  subtitle,
  icon,
  to,
  action,
  chips = [],
  beta,
  live,
}: DashboardCardProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const surface = isDark ? "#181411" : SURFACE;
  const iconSurface = isDark ? "#211B16" : COPPER_SOFT;
  const borderColor = isDark ? "rgba(224,177,132,0.24)" : BORDER_SOFT;
  const hoverBorderColor = isDark ? "rgba(224,177,132,0.38)" : "rgba(184,115,51,0.44)";
  const mutedText = isDark ? "text.secondary" : MUTED;
  const copperText = isDark ? "#F2C69B" : COPPER_DARK;

  return (
    <Card
      component={RouterLink}
      to={to}
      elevation={0}
      aria-label={`${action}: ${title}`}
      sx={{
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${borderColor}`,
        bgcolor: surface,
        boxShadow: isDark ? "0 18px 54px rgba(0,0,0,0.34)" : "0 10px 28px rgba(74, 47, 25, 0.055)",
        color: "text.primary",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        textDecoration: "none",
        transition: "transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          borderColor: hoverBorderColor,
          boxShadow: isDark ? "0 22px 60px rgba(0,0,0,0.40)" : "0 14px 34px rgba(74, 47, 25, 0.09)",
          transform: "translateY(-1px)",
          "& .dashboard-card-arrow": {
            transform: "translateX(2px)",
          },
        },
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2.25, md: 2.5 },
          display: "grid",
          flex: 1,
          gridTemplateRows: "auto auto minmax(64px, 1fr) auto",
          rowGap: 1.6,
        }}
      >
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: UI_RADIUS.card,
                bgcolor: iconSurface,
                color: copperText,
                display: "grid",
                placeItems: "center",
              }}
            >
              {icon}
            </Box>
            <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap" justifyContent="flex-end">
              {live && <MetricChip label="LIVE" tone="green" />}
              {beta && <MetricChip label="BETA" tone="copper" />}
            </Stack>
          </Stack>

          <Typography variant="overline" sx={{ display: "block", mt: 1.65, color: mutedText, fontWeight: 900, letterSpacing: 1.1, lineHeight: 1.3 }}>
            {eyebrow}
          </Typography>
          {descriptor && (
            <Typography variant="caption" sx={{ display: "block", color: copperText, fontWeight: 850, mt: 0.15, lineHeight: 1.25 }}>
              {descriptor}
            </Typography>
          )}
          <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: 0, mt: 1.25, fontSize: { xs: 36, md: 40 }, lineHeight: 1.02 }}>
            {value}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.65, lineHeight: 1.18 }}>
            {title}
          </Typography>
        </Box>

        <Typography
          data-testid="dashboard-card-subtitle"
          variant="body2"
          sx={{
            color: mutedText,
            lineHeight: 1.45,
            minHeight: "42px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {subtitle}
        </Typography>

        <Stack
          data-testid="dashboard-card-chips"
          direction="row"
          spacing={0.85}
          useFlexGap
          flexWrap="wrap"
          alignContent="flex-start"
          sx={{ minHeight: 64 }}
        >
          {chips.map((chip) => (
            <MetricChip key={chip.label} label={chip.label} tone={chip.tone} />
          ))}
        </Stack>

        <Stack
          data-testid="dashboard-card-action"
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            alignSelf: "flex-start",
            color: copperText,
            fontWeight: 900,
          }}
        >
          <Typography variant="button" sx={{ textTransform: "none", fontWeight: 900, lineHeight: 1.4 }}>
            {action}
          </Typography>
          <ArrowForwardIcon className="dashboard-card-arrow" fontSize="small" sx={{ transition: "transform 160ms ease" }} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { flags } = useFeatureFlags();
  const { permissions, can } = useUser();
  const canReadAgents = Boolean(can?.(PERMISSIONS.AGENTS_READ));
  const canCreateAgents = Boolean(can?.(PERMISSIONS.AGENTS_CREATE));
  const canReadWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_READ));
  const canCreateWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_CREATE));
  const canReadTools = Boolean(can?.(PERMISSIONS.TOOLS_READ));
  const canReadUsers = Boolean(can?.(PERMISSIONS.USERS_READ));
  const canReadRoles = Boolean(can?.(PERMISSIONS.ROLES_READ));
  const canReadAudit = Boolean(can?.(PERMISSIONS.AUDIT_READ));
  const canUsePlatformTelemetry = canViewPlatformTelemetry(permissions);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [needlepath, setNeedlepath] = useState<any>(null);
  const [mnemo, setMnemo] = useState<any>(null);
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function loadDashboard() {
      setLoading(true);
      setError("");
      try {
        const [agentData, workflowData, toolData, needlepathData, mnemoData, configData] = await Promise.allSettled([
          canReadAgents ? listAgents() : Promise.resolve({ agents: [] }),
          canReadWorkflows ? listWorkflows() : Promise.resolve([]),
          canReadTools ? listTools() : Promise.resolve([]),
          canUsePlatformTelemetry ? getNeedlepathMetricsSummary(24) : Promise.resolve(null),
          canUsePlatformTelemetry ? getMnemoLiveDashboard(24, 80) : Promise.resolve(null),
          canUsePlatformTelemetry ? getConfig() : Promise.resolve(null),
        ]);
        if (!mounted) return;
        if (agentData.status === "fulfilled") setAgents(agentData.value?.agents || []);
        if (workflowData.status === "fulfilled") setWorkflows(workflowData.value || []);
        if (toolData.status === "fulfilled") setTools(toolData.value || []);
        if (needlepathData.status === "fulfilled") setNeedlepath(needlepathData.value || null);
        if (mnemoData.status === "fulfilled") setMnemo(mnemoData.value || null);
        if (configData.status === "fulfilled") setConfig(configData.value || null);
        if (
          [
            canReadAgents ? agentData : null,
            canReadWorkflows ? workflowData : null,
            canReadTools ? toolData : null,
            canUsePlatformTelemetry ? needlepathData : null,
            canUsePlatformTelemetry ? mnemoData : null,
          ].some((result) => result?.status === "rejected")
        ) {
          setError("Some dashboard data could not be loaded.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadDashboard();
    return () => {
      mounted = false;
    };
  }, [canReadAgents, canReadWorkflows, canReadTools, canUsePlatformTelemetry]);

  const metrics = useMemo(() => {
    const readyAgents = agents.filter((agent) => normalizeStatus(agent.status) === "ready").length;
    const setupAgents = agents.filter(agentNeedsApiSetup).length;
    const builtInAgents = agents.filter((agent) => agent.agent_source === "catalog").length;
    const activeWorkflows = workflows.filter((workflow) =>
      ["active", "ready", "published"].includes(normalizeStatus(workflow.status)),
    ).length;
    const publishedTools = tools.filter((tool) => normalizeStatus(tool.status) === "published").length;
    const setupTools = tools.filter(toolNeedsSetup).length;
    const forgeTools = tools.filter((tool) => Boolean(tool.exportable)).length;
    const needlepathAgentTotals = needlepath?.agent_totals || {};
    const needlepathSaved = toNumber(
      needlepathAgentTotals.tokens_saved,
      needlepath?.tokens_saved,
      needlepath?.summary?.total_tokens_saved,
    );
    const needlepathObserved = toNumber(
      needlepathAgentTotals.total_runs,
      needlepath?.total_runs,
      needlepath?.summary?.observed_count,
    );
    const needlepathApplied = toNumber(needlepathAgentTotals.applied_runs, needlepath?.applied_runs);
    const needlepathAvailable = needlepath?.available !== false;
    const needlepathEnabled = config?.needlepath?.enabled !== false;
    const needlepathLive = Boolean(needlepathAvailable && needlepathEnabled && needlepath);
    const mnemoSummary = mnemo?.summary || {};
    const mnemoAppliedRuns = toNumber(mnemoSummary.memory_applied_runs, mnemoSummary.injection_runs);
    const mnemoEligibleRuns = toNumber(mnemoSummary.memory_eligible_runs, mnemoSummary.memory_available_runs);
    const mnemoAppliedRatio =
      mnemoSummary.memory_applied_ratio !== undefined
        ? toNumber(mnemoSummary.memory_applied_ratio)
        : mnemoEligibleRuns
          ? mnemoAppliedRuns / mnemoEligibleRuns
          : 0;
    const mnemoApplied = Math.round(mnemoAppliedRatio * 100);
    const mnemoInjected = toNumber(mnemoSummary.records_injected);
    const mnemoRuns = mnemoAppliedRuns;
    const mnemoChecks = toNumber(mnemoSummary.memory_checks, mnemoSummary.injection_runs);
    const mnemoLive = Boolean(mnemo);

    return {
      readyAgents,
      setupAgents,
      builtInAgents,
      activeWorkflows,
      publishedTools,
      setupTools,
      forgeTools,
      needlepathSaved,
      needlepathObserved,
      needlepathApplied,
      needlepathLive,
      mnemoApplied,
      mnemoInjected,
      mnemoRuns,
      mnemoChecks,
      mnemoLive,
    };
  }, [agents, workflows, tools, needlepath, mnemo, config]);

  const workflowCreatePath = canCreateWorkflows && flags.enableWorkflows ? "/workflow/intent-build" : "/workflows";
  const totalRuntimeSignals = canUsePlatformTelemetry ? metrics.needlepathObserved + metrics.mnemoChecks : 0;
  const attentionCount = loading ? 0 : metrics.setupTools + metrics.setupAgents;

  const attentionItems = useMemo<SignalItemProps[]>(() => {
    if (loading) {
      return [
        {
          title: "Loading dashboard signals",
          detail: "Fetching current catalog and runtime state.",
          tone: "neutral",
        },
      ];
    }

    const items: SignalItemProps[] = [];

    if (metrics.setupTools > 0) {
      items.push({
        title: `${metrics.setupTools} tools need setup`,
        detail: "Credentials or setup requirements are blocking catalog readiness.",
        tone: "amber",
        to: "/tools",
        action: "Review tools",
      });
    }

    if (metrics.setupAgents > 0) {
      items.push({
        title: `${metrics.setupAgents} agents need setup`,
        detail: "Some agents still need API configuration before they are ready.",
        tone: "amber",
        to: "/agents?status=setup_required",
        action: "Review agents",
      });
    }

    if (items.length === 0) {
      items.push({
        title: "No blocking setup items",
        detail: "Setup-sensitive catalogs are reporting normally.",
        tone: "green",
      });
    }

    return items;
  }, [loading, metrics]);

  const activityItems = useMemo<SignalItemProps[]>(() => {
    const items: SignalItemProps[] = [];

    if (canReadAgents) {
      items.push({
        title: "Agent catalog",
        detail: `${pluralize(metrics.readyAgents, "agent")} ready, ${pluralize(metrics.builtInAgents, "built-in")} available.`,
        tone: metrics.readyAgents ? "green" : "neutral",
      });
    }

    if (canReadWorkflows || canCreateWorkflows) {
      items.push({
        title: "Workflow catalog",
        detail:
          workflows.length > 0
            ? `${pluralize(metrics.activeWorkflows, "workflow")} active out of ${workflows.length}.`
            : canCreateWorkflows
              ? "No saved workflow activity yet."
              : "Workflow catalog is available for review.",
        tone: metrics.activeWorkflows ? "green" : workflows.length === 0 ? "amber" : "neutral",
      });
    }

    if (canReadTools) {
      items.push({
        title: "Tool Forge",
        detail: `${pluralize(metrics.publishedTools, "tool")} published, ${pluralize(metrics.setupTools, "setup item")} open.`,
        tone: metrics.setupTools ? "amber" : "green",
      });
    }

    if (canReadUsers || canReadRoles) {
      items.push({
        title: "Users and roles",
        detail: "Directory and role catalog are available for access review.",
        tone: "blue",
        to: canReadUsers ? "/users" : "/roles",
        action: canReadUsers ? "Open users" : "Open roles",
      });
    }

    if (canReadAudit) {
      items.push({
        title: "Audit evidence",
        detail: "Security and access events are available for review.",
        tone: "blue",
        to: "/audit-logs",
        action: "Open audit logs",
      });
    }

    if (canUsePlatformTelemetry) {
      items.push({
        title: "Optimization and memory",
        detail: `${compactNumber(totalRuntimeSignals)} runtime signals across Needlepath and Mnemo.`,
        tone: totalRuntimeSignals ? "green" : "neutral",
      });
    }

    if (items.length === 0) {
      items.push({
        title: "Limited dashboard",
        detail: "No dashboard areas are available for the current role.",
        tone: "neutral",
      });
    }

    return items;
  }, [
    canCreateWorkflows,
    canReadAgents,
    canReadAudit,
    canReadRoles,
    canReadTools,
    canReadUsers,
    canReadWorkflows,
    canUsePlatformTelemetry,
    metrics,
    totalRuntimeSignals,
    workflows.length,
  ]);

  const governanceAreas = useMemo(
    () => [
      ...(canReadUsers ? [{ label: "Users", tone: "blue" as Tone, to: "/users" }] : []),
      ...(canReadRoles ? [{ label: "Roles", tone: "green" as Tone, to: "/roles" }] : []),
      ...(canReadAudit ? [{ label: "Audit", tone: "copper" as Tone, to: "/audit-logs" }] : []),
    ],
    [canReadAudit, canReadRoles, canReadUsers],
  );
  const governancePath = governanceAreas[0]?.to || "/dashboard";

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Dashboard
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mt: 0.4 }}>
            Live operating view for catalog readiness, workflow coverage, optimization, and memory.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          {canCreateAgents && (
            <Button
              component={RouterLink}
              to="/agent/create"
              variant="contained"
              startIcon={<SmartToyOutlinedIcon />}
              sx={{
                borderRadius: UI_RADIUS.button,
                bgcolor: "primary.main",
                boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
                color: "#fff",
                fontWeight: 900,
                minHeight: 44,
                minWidth: { sm: 168 },
                px: 2.4,
                textTransform: "none",
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  bgcolor: "primary.dark",
                  boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
                },
              }}
            >
              Create Agent
            </Button>
          )}
          {flags.enableWorkflows && canCreateWorkflows && (
            <Button
              component={RouterLink}
              to="/workflow/intent-build"
              variant="contained"
              startIcon={<AutoAwesomeOutlinedIcon />}
              sx={{
                borderRadius: UI_RADIUS.button,
                bgcolor: "primary.main",
                boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
                color: "#fff",
                fontWeight: 900,
                minHeight: 44,
                minWidth: { sm: 188 },
                px: 2.4,
                textTransform: "none",
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  bgcolor: "primary.dark",
                  boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
                },
              }}
            >
              Create Workflow
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberOutlinedIcon />}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1fr) 300px",
            xl: "minmax(0, 1fr) 320px",
          },
          gap: PAGE_SHELL.sectionGap,
          alignItems: "start",
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {canReadAgents && (
                <DashboardCard
                  eyebrow="Agent Catalog"
                  descriptor="Agent library"
                  title="Agent collections"
                  value={agents.length}
                  subtitle="Built-in and custom agents grouped by the work they perform."
                  icon={<SmartToyOutlinedIcon />}
                  to="/agents"
                  action="Open Agent Catalog"
                  chips={[
                    { label: `${metrics.readyAgents} ready`, tone: "green" },
                    { label: `${metrics.setupAgents} setup`, tone: metrics.setupAgents ? "amber" : "neutral" },
                    { label: `${metrics.builtInAgents} built-in`, tone: "green" },
                  ]}
                />
              )}
              {(canReadWorkflows || canCreateWorkflows) && (
                <DashboardCard
                  eyebrow="Workflow Catalog"
                  descriptor="Automation coverage"
                  title={workflows.length ? "Reusable workflows" : "No workflows yet"}
                  value={workflows.length}
                  subtitle={
                    workflows.length
                      ? "Saved automations, approvals, and repeatable operating flows."
                      : canCreateWorkflows
                        ? "Create one from intent to turn repeatable work into an operating flow."
                        : "No saved workflow activity yet."
                  }
                  icon={<AccountTreeOutlinedIcon />}
                  to={workflows.length || !canCreateWorkflows ? "/workflows" : workflowCreatePath}
                  action={workflows.length || !canCreateWorkflows ? "Open Workflows" : "Create Workflow"}
                  chips={[
                    { label: `${metrics.activeWorkflows} active`, tone: metrics.activeWorkflows ? "green" : "neutral" },
                    { label: `${Math.max(workflows.length - metrics.activeWorkflows, 0)} drafts`, tone: "neutral" },
                  ]}
                />
              )}
              {canReadTools && (
                <DashboardCard
                  eyebrow="Tool Forge"
                  descriptor="Tools & connectors"
                  title="Catalog readiness"
                  value={tools.length}
                  subtitle="Approved tools, generated bundles, setup requirements, and connector readiness."
                  icon={<ConstructionOutlinedIcon />}
                  to="/tools"
                  action="Open Tool Catalog"
                  chips={[
                    { label: `${metrics.publishedTools} published`, tone: "green" },
                    { label: `${metrics.setupTools} need setup`, tone: metrics.setupTools ? "amber" : "neutral" },
                    { label: `${metrics.forgeTools} forge-made`, tone: "copper" },
                  ]}
                />
              )}
              {governanceAreas.length > 0 && (
                <DashboardCard
                  eyebrow="Governance"
                  descriptor="Access review"
                  title="Users, roles, and audit"
                  value={governanceAreas.length}
                  subtitle="Review account access, role coverage, and audit evidence from one place."
                  icon={<AdminPanelSettingsOutlinedIcon />}
                  to={governancePath}
                  action="Open Governance"
                  chips={governanceAreas.map(({ label, tone }) => ({ label, tone }))}
                />
              )}
              {canUsePlatformTelemetry && (
                <>
                  <DashboardCard
                    eyebrow="Needlepath"
                    descriptor="Prompt optimization"
                    title="Tokens saved"
                    value={compactNumber(metrics.needlepathSaved)}
                    subtitle={
                      metrics.needlepathObserved
                        ? "Optimization health across eligible agent activity."
                        : "Watching eligible traffic before optimization savings appear."
                    }
                    icon={<SpeedOutlinedIcon />}
                    to="/needlepath"
                    action="Open Needlepath"
                    beta
                    live={metrics.needlepathLive}
                    chips={[
                      { label: `${metrics.needlepathObserved} observed`, tone: metrics.needlepathObserved ? "blue" : "neutral" },
                      { label: `${metrics.needlepathApplied} applied`, tone: metrics.needlepathApplied ? "green" : "neutral" },
                      {
                        label: metrics.needlepathSaved > 0 ? "saving tokens" : "watching traffic",
                        tone: metrics.needlepathSaved > 0 ? "green" : "neutral",
                      },
                    ]}
                  />
                  <DashboardCard
                    eyebrow="Mnemo"
                    descriptor="Memory layer"
                    title="Memory applied"
                    value={`${metrics.mnemoApplied}%`}
                    subtitle={
                      metrics.mnemoChecks
                        ? "Memory capture, durable records, and runtime injection signal."
                        : "Waiting for eligible runs before memory influence can be measured."
                    }
                    icon={<MemoryOutlinedIcon />}
                    to="/mnemo"
                    action="Open Mnemo"
                    beta
                    live={metrics.mnemoLive}
                    chips={[
                      { label: `${metrics.mnemoRuns} runs influenced`, tone: metrics.mnemoRuns ? "green" : "neutral" },
                      { label: `${metrics.mnemoInjected} records injected`, tone: metrics.mnemoInjected ? "green" : "neutral" },
                      { label: `${metrics.mnemoChecks} checks`, tone: metrics.mnemoChecks ? "blue" : "neutral" },
                    ]}
                  />
                </>
              )}
            </Box>
          </Box>
        </Stack>

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <SectionShell
            title="Signals"
            description={attentionCount > 0 ? `${pluralize(attentionCount, "setup signal")} open.` : "No blocking setup signals."}
            testId="dashboard-attention-panel"
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography component="h3" variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 950, letterSpacing: 0.4, textTransform: "uppercase" }}>
                  Needs attention
                </Typography>
                <Stack divider={<Divider flexItem sx={{ borderColor: "divider" }} />}>
                  {attentionItems.map((item) => (
                    <SignalItem key={item.title} {...item} />
                  ))}
                </Stack>
              </Box>
              <Divider sx={{ borderColor: "divider" }} />
              <Box>
                <Typography component="h3" variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 950, letterSpacing: 0.4, textTransform: "uppercase" }}>
                  Activity
                </Typography>
                <Stack divider={<Divider flexItem sx={{ borderColor: "divider" }} />}>
                  {activityItems.map((item) => (
                    <SignalItem key={item.title} {...item} />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </SectionShell>
        </Stack>
      </Box>
    </Box>
  );
}
